import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  Typography,
  useRadioGroup,
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React from 'react'
import { Controller, FieldErrors, UseControllerOptions, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

const StyledFormLabel = styled(FormLabel)`
  && {
    margin-bottom: 10px;
  }
`

const StyledLabelContainer = styled.div`
  margin-right: 10px;
`

const StyledFormControlLabel = styled(FormControlLabel)<{ $isChecked: boolean }>`
  background: ${({ theme, $isChecked }) =>
    $isChecked ? `${theme.colors.brand.white}` : 'rgba(255,255,255, 0.66)'};
  border: ${({ theme, $isChecked }) =>
    $isChecked ? `1px solid ${theme.colors.brand.blue}` : 'none'};
  border-radius: 4px;
  box-sizing: border-box;
  margin-left: 0;
  margin-right: 0;
  padding: 7px 0;
  &&:first-of-type {
    margin-bottom: 15px;
  }

  && .MuiFormControlLabel-label {
    font-size: 12px;
    white-space: normal;
  }

  &.MuiFormControlLabel-root {
    margin-left: 0;
  }
`

export interface RadioGroupProps
  extends Omit<
    MuiRadioGroupProps,
    'checked' | 'defaultChecked' | 'defaultValue' | 'inputRef' | 'onBlur' | 'onChange' | 'value'
  > {
  defaultValue?: string
  disabled?: boolean
  errors?: FieldErrors
  name: string
  rules?: UseControllerOptions['rules']
}

export const RenderEntireImageAsCtaRadioGroup: React.FC<RadioGroupProps> = ({
  className = '',
  defaultValue,
  disabled = false,
  name,
  rules,
  ...other
}) => {
  // not checking errors since we will always specify a default value
  const { control } = useFormContext()
  const CustomFormControlLabel = (props: FormControlLabelProps) => {
    const radioGroup = useRadioGroup()
    let checked = false
    if (radioGroup) {
      checked = radioGroup.value === props.value
    }

    return <StyledFormControlLabel $isChecked={checked} {...props} />
  }
  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ onBlur, onChange, value }) => {
        return (
          <FormControl component="fieldset" disabled={disabled}>
            <StyledFormLabel>
              <Typography variant="body2">Use Image as Banner Background?</Typography>
            </StyledFormLabel>
            <MuiRadioGroup
              className={className}
              {...other}
              name={name}
              onBlur={onBlur}
              onChange={(e) => onChange(e.target.value)}
              row
              value={value}
            >
              <CustomFormControlLabel
                control={<Radio checkedIcon={<CheckCircleIcon />} />}
                label={
                  <StyledLabelContainer>
                    <b>Yes, use image as background</b>
                    <br />
                    You may select or customize text to display within the banner.
                    <br />
                    <i>
                      Note: The image will be cropped and scaled to fill the hero banner background.
                      (Minimum width 2560px, minimum height 600px)
                    </i>
                  </StyledLabelContainer>
                }
                value="no"
              />
              <CustomFormControlLabel
                control={<Radio checkedIcon={<CheckCircleIcon />} />}
                label={
                  <StyledLabelContainer>
                    <b>No, use entire image as clickable banner</b>
                    <br />
                    <i>
                      Note: The image will not be cropped. (We recommend a minimum width of 2560px
                      with an aspect ratio of 2:1 or wider)
                    </i>
                  </StyledLabelContainer>
                }
                value="yes"
              />
            </MuiRadioGroup>
          </FormControl>
        )
      }}
      rules={rules}
    />
  )
}
